import moment from "moment"

import { CONTRACT_NIGHTS, HOTEL_CONTRACTS } from "./hotel-contracts"

// Turns a Firestore timestamp into a moment, or null when it is missing.
function getTimestampMoment(timestamp) {
	if (!timestamp?.seconds) return null
	const parsed = moment(timestamp.seconds, "X")
	return parsed.isValid() ? parsed : null
}

// The number of guests a booking puts into a hotel. The group size on the
// booking is the authoritative headcount, so fall back to the named guests only
// when it has not been filled in yet.
function getGuestCount(booking) {
	const groupSize = Number(booking?.group_size) || 0
	if (groupSize > 0) return groupSize
	return Array.isArray(booking?.group_names) ? booking.group_names.length : 0
}

function createHotelRow(resortID, name) {
	return {
		resortID,
		name,
		contract: HOTEL_CONTRACTS[resortID] || null,
		nights: {},
		guests: 0,
		roomNights: 0,
		roomNightsOutsideBlock: 0,
		stays: 0,
		bookingIDs: new Set(),
	}
}

/**
 * Works out, per hotel, how many rooms and guests are in house on each night of
 * the event, and how many room nights that adds up to across the whole stay.
 *
 * Room nights are counted per stay (rooms on the booking x nights booked), not
 * per guest, because that is the unit every hotel contract is written in.
 *
 * @param {array} bookings Bookings from the Masters 2027 container
 * @returns {object} Rows keyed by hotel, plus the totals across all of them
 */
export function buildHotelOccupancy(bookings = []) {
	const rows = {}
	const guestCountByBooking = {}

	// Every contracted hotel gets a row whether or not anything is booked into
	// it yet, so an empty block is visible rather than silently absent.
	Object.entries(HOTEL_CONTRACTS).forEach(([resortID, contract]) => {
		rows[resortID] = createHotelRow(resortID, contract.contract_name)
	})

	;(Array.isArray(bookings) ? bookings : []).forEach((booking) => {
		const guestCount = getGuestCount(booking)
		guestCountByBooking[booking?.bookingID] = guestCount

		;(Array.isArray(booking?.hotels) ? booking.hotels : []).forEach((hotel) => {
			const resortID = hotel?.resortID
			if (!resortID) return

			const checkIn = getTimestampMoment(hotel?.check_in)
			const nights = Number(hotel?.nights) || 0
			const rooms = Array.isArray(hotel?.rooms) ? hotel.rooms.length : 0

			// A stay with no dates, no nights or no rooms cannot contribute room
			// nights, so there is nothing to place on the grid.
			if (!checkIn || nights <= 0 || rooms <= 0) return

			if (!rows[resortID]) rows[resortID] = createHotelRow(resortID, hotel?.hotel_name || "Unknown hotel")
			const row = rows[resortID]

			row.stays++
			row.roomNights += rooms * nights

			// Guests are counted once per booking per hotel, so a booking split
			// across two rooms at the same hotel is not counted twice.
			if (!row.bookingIDs.has(booking.bookingID)) {
				row.bookingIDs.add(booking.bookingID)
				row.guests += guestCount
			}

			for (let night = 0; night < nights; night++) {
				const date = checkIn.clone().add(night, "days").format("YYYY-MM-DD")

				if (!row.nights[date]) row.nights[date] = { rooms: 0, guests: 0 }
				row.nights[date].rooms += rooms
				row.nights[date].guests += guestCount

				// Nights slept outside the contracted window are still ours to
				// service, but they do not count towards filling a block.
				if (!CONTRACT_NIGHTS.includes(date)) row.roomNightsOutsideBlock += rooms
			}
		})
	})

	// Contracted hotels first, then anything else by room nights used.
	const ordered = Object.values(rows).sort((a, b) => {
		if (!!a.contract !== !!b.contract) return a.contract ? -1 : 1
		if (a.contract && b.contract) return b.contract.room_nights - a.contract.room_nights
		return b.roomNights - a.roomNights
	})

	return { rows: ordered, guestCountByBooking }
}

/**
 * Totals a set of hotel rows, counting each booking's guests once however many
 * of those hotels it touches. Kept separate from the build above so the totals
 * always match whichever rows are actually on screen.
 *
 * @param {array} rows Hotel rows from buildHotelOccupancy
 * @param {object} guestCountByBooking Guests per booking, from the same call
 * @returns {object} Per-night and overall totals for those rows
 */
export function summariseRows(rows = [], guestCountByBooking = {}) {
	const totals = {
		nights: {},
		guests: 0,
		roomNights: 0,
		roomNightsOutsideBlock: 0,
		contractedRoomNights: 0,
	}
	const countedBookings = new Set()

	rows.forEach((row) => {
		totals.roomNights += row.roomNights
		totals.roomNightsOutsideBlock += row.roomNightsOutsideBlock
		totals.contractedRoomNights += row.contract?.room_nights || 0

		row.bookingIDs.forEach((bookingID) => {
			if (countedBookings.has(bookingID)) return
			countedBookings.add(bookingID)
			totals.guests += guestCountByBooking[bookingID] || 0
		})

		Object.entries(row.nights).forEach(([date, night]) => {
			if (!totals.nights[date]) totals.nights[date] = { rooms: 0, guests: 0 }
			totals.nights[date].rooms += night.rooms
			totals.nights[date].guests += night.guests
		})
	})

	return totals
}

// The room nights used against a block, ignoring anything slept outside it.
export function getRoomNightsInBlock(row) {
	return row.roomNights - row.roomNightsOutsideBlock
}

// How full a block is, as a whole percentage. Null when there is no contract.
export function getFillPercentage(row) {
	if (!row.contract?.room_nights) return null
	return Math.round((getRoomNightsInBlock(row) / row.contract.room_nights) * 100)
}

// Green while a block is comfortably on track, amber as it slips, red once we
// are either badly short or overselling what we hold.
export function getFillBadgeType(fill) {
	if (fill === null) return "INFO"
	if (fill > 100) return "NEGATIVE"
	if (fill >= 85) return "POSITIVE"
	if (fill >= 50) return "AMBER"
	return "NEGATIVE"
}

export function formatNightLabel(date) {
	return moment(date, "YYYY-MM-DD").format("ddd D MMM")
}

export function formatShortNightLabel(date) {
	return moment(date, "YYYY-MM-DD").format("ddd D")
}
